<script lang="ts">
  import type { Snippet } from "svelte";
  import { fade, fly } from "svelte/transition";

  let {
    show = $bindable(false),
    title = "",
    size = "md",
    onClose,
    children,
  }: {
    show?: boolean;
    title?: string;
    size?: "sm" | "md" | "lg";
    onClose?: () => void;
    children?: Snippet;
  } = $props();

  const sizes = {
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg",
  };

  function close() {
    show = false;
    onClose?.();
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === "Escape") {
      close();
    }
  }
</script>

<svelte:window onkeydown={handleKeydown} />

{#if show}
  <div class="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
    <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
      <!-- Backdrop -->
      <button
        type="button"
        class="fixed inset-0 transition-opacity cursor-default w-full h-full modal__backdrop"
        transition:fade={{ duration: 200 }}
        onclick={close}
        aria-label="모달 닫기"
      ></button>

      <!-- Modal content -->
      <div
        class="relative rounded-2xl {sizes[size]} w-full p-6 text-left transform transition-all modal__panel"
        transition:fly={{ y: 20, duration: 300 }}
      >
        {#if title}
          <div class="flex items-center justify-between mb-6 modal__header">
            <h2 class="text-lg font-semibold modal__title">{title}</h2>
            <button
              onclick={close}
              class="p-1.5 rounded-lg transition-colors cursor-pointer modal__close"
              aria-label="닫기"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        {/if}

        {@render children?.()}
      </div>
    </div>
  </div>
{/if}

<style>
  .modal__backdrop {
    background: rgba(15, 23, 42, 0.48);
    backdrop-filter: blur(4px);
  }

  .modal__panel {
    background: var(--surface);
    color: var(--text-primary);
    border: 1px solid var(--border);
    box-shadow: 0 24px 80px rgba(15, 23, 42, 0.18);
  }

  .modal__header {
    border-bottom: 1px solid var(--border);
    padding-bottom: 1rem;
  }

  .modal__title {
    color: var(--text-primary);
  }

  .modal__close {
    color: var(--text-muted);
  }

  .modal__close:hover {
    color: var(--text-primary);
    background: color-mix(in srgb, var(--surface-hover) 88%, transparent);
  }

  @media (prefers-color-scheme: dark) {
    .modal__backdrop {
      background: rgba(0, 0, 0, 0.68);
    }

    .modal__panel {
      box-shadow: 0 24px 80px rgba(0, 0, 0, 0.55);
    }
  }
</style>
